import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Alert,
  Space,
  Row,
  Col,
  Divider,
  Result,
} from 'antd';
import {
  LockOutlined,
  MailOutlined,
  CheckCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { userAPI } from '../services/api';
import { validatePassword } from '../utils/validation';

const { Title, Text, Paragraph } = Typography;

const AcceptInvitationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState<string>('');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    // Extract token from URL query parameters
    const urlParams = new URLSearchParams(location.search);
    const inviteToken = urlParams.get('token');
    
    if (!inviteToken) {
      setError('Invalid invitation link. No token found.');
      return;
    }
    
    setToken(inviteToken);
  }, [location]);

  const handleAcceptInvitation = async (values: any) => {
    try {
      setLoading(true);
      setError('');

      await userAPI.acceptInvitation({
        token,
        password: values.password,
        confirmPassword: values.confirmPassword,
      });

      setAccepted(true);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to accept invitation');
    } finally {
      setLoading(false);
    }
  };

  const validatePasswordMatch = (_: any, value: string) => {
    const password = form.getFieldValue('password');
    if (value && password !== value) {
      return Promise.reject(new Error('Passwords do not match'));
    }
    return Promise.resolve();
  };

  const validatePasswordStrength = (_: any, value: string) => {
    if (!value) return Promise.resolve();
    
    const { isValid, message } = validatePassword(value);
    if (!isValid) {
      return Promise.reject(new Error(message));
    }
    return Promise.resolve();
  };

  if (!token && !error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)'
      }}>
        <Card loading style={{ width: 400 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)'
      }}>
        <Card style={{ width: 500, textAlign: 'center', borderRadius: '12px' }}>
          <Result
            status="error"
            title="Invalid Invitation"
            subTitle={error}
            extra={
              <Button 
                type="primary" 
                onClick={() => navigate('/login')}
                style={{ background: '#1e40af', borderColor: '#1e40af' }}
              >
                Go to Login
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  if (accepted) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)'
      }}>
        <Card style={{ width: 500, textAlign: 'center', borderRadius: '12px' }}>
          <Result
            status="success"
            title="Welcome to Texo!"
            subTitle="Your invitation has been accepted successfully. You can now log in with your email and password."
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            extra={
              <Button 
                type="primary" 
                size="large" 
                onClick={() => navigate('/login')}
                style={{ background: '#1e40af', borderColor: '#1e40af', height: '48px' }}
              >
                Go to Login
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
      padding: '20px'
    }}>
      <Card style={{ width: '100%', maxWidth: 500, borderRadius: '12px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ 
            fontSize: '48px',
            fontWeight: '800',
            letterSpacing: '-0.5px',
            color: '#1e40af',
            marginBottom: '24px'
          }}>
            TEXO
          </div>
          <Title level={2} style={{ margin: '0 0 8px 0', color: '#0f172a' }}>
            Welcome to the Team!
          </Title>
          <Text type="secondary" style={{ fontSize: '16px' }}>
            Complete your account setup to get started
          </Text>
        </div>

        <Divider />

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleAcceptInvitation}
          size="large"
        >
          <Paragraph style={{ marginBottom: 24 }}>
            <MailOutlined style={{ marginRight: 8 }} />
            Please set a secure password for your account. You'll use this to log in along with your email address.
          </Paragraph>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter your password' },
              { validator: validatePasswordStrength },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Create a secure password"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            rules={[
              { required: true, message: 'Please confirm your password' },
              { validator: validatePasswordMatch },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Confirm your password"
              autoComplete="new-password"
            />
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Password requirements:
            </Text>
            <ul style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: 4,
              paddingLeft: 16 
            }}>
              <li>At least 8 characters long</li>
              <li>Contains uppercase and lowercase letters</li>
              <li>Contains at least one number</li>
            </ul>
          </div>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{ 
                height: 48, 
                background: '#1e40af', 
                borderColor: '#1e40af',
                fontWeight: 600,
                fontSize: '16px'
              }}
            >
              Accept Invitation & Create Account
            </Button>
          </Form.Item>
        </Form>

        <Divider />

        <div style={{ textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Already have an account?{' '}
            <Button type="link" size="small" onClick={() => navigate('/login')}>
              Sign in here
            </Button>
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default AcceptInvitationPage;
